import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type { User, UserCreate, UserUpdate, PasswordChange, Role, PaginatedResponse } from '@/types'

interface UseUsersParams {
  page?: number
  perPage?: number
  search?: string
  roleId?: string
  isActive?: boolean | null
}

export function useUsers(params: UseUsersParams = {}) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const query: Record<string, string | number | boolean> = {
        page: params.page || 1,
        per_page: params.perPage || 20,
      }
      if (params.search) query.search = params.search
      if (params.roleId) query.role_id = params.roleId
      if (params.isActive !== null && params.isActive !== undefined) query.is_active = params.isActive
      const { data } = await api.get<PaginatedResponse<User>>('/v1/users/', { params: query })
      return data
    },
  })
}

export function useUser(userId: string | null) {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: async () => {
      const { data } = await api.get<User>(`/v1/users/${userId}`)
      return data
    },
    enabled: !!userId,
  })
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await api.get<{ items: Role[]; total: number }>('/v1/users/roles')
      return data.items
    },
    staleTime: 1000 * 60 * 30,
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (userData: UserCreate) => {
      const { data } = await api.post<User>('/v1/users/', userData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, data: userData }: { userId: string; data: UserUpdate }) => {
      const { data } = await api.put<User>(`/v1/users/${userId}`, userData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/v1/users/${userId}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useHardDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/v1/users/${userId}/permanent`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ userId, data: pwData }: { userId: string; data: PasswordChange }) => {
      await api.post(`/v1/users/${userId}/change-password`, pwData)
    },
  })
}

export function useLastLogins() {
  return useQuery({
    queryKey: ['users', 'last-logins'],
    queryFn: async () => {
      const { data } = await api.get('/v1/users/last-logins')
      return data.items as Array<{
        id: string
        first_name: string
        last_name: string
        email: string
        role_name: string
        last_login_at: string | null
        is_active: boolean
      }>
    },
    retry: false,
  })
}

export function useResetTotp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      const { data } = await api.put(`/v1/users/${userId}/totp-reset?enabled=${enabled}`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['last-logins'] })
    },
  })
}
